import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Send, Plus, Trash2, ChevronDown, ChevronUp, ArrowRight,
  MessageSquare, GitBranch, Share2, FileCode, X, Loader2,
  Copy, Check as CheckIcon, ChevronRight, ChevronLeft, Zap, CheckCircle,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { getFileLanguage, cn } from '../lib/utils';
import { SEO } from '../components/SEO';
import { API_URL } from '../lib/config';
function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('cp_token')}`, 'Content-Type': 'application/json' };
}

interface CodeReference {
  filePath: string;
  startLine: number;
  endLine: number;
  content: string;
  score?: number;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  references?: CodeReference[];
  streaming?: boolean;
}

interface Conversation {
  _id: string;
  title: string;
  updatedAt: string;
}

interface Repo {
  _id: string;
  name: string;
  indexingStatus: string;
  fileCount?: number;
}

/* ─── Code reference card ───────────────────────────────────── */

function CodeRefCard({ codeRef: r }: { codeRef: CodeReference }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const lang = getFileLanguage(r.filePath);

  function copyContent() {
    navigator.clipboard.writeText(r.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="code-block mt-2">
      <button
        onClick={() => setOpen(o => !o)}
        className="code-header w-full text-left transition-colors group"
        style={{ backgroundColor: 'transparent' }}
        onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--overlay-sm)')}
        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        <FileCode className="w-3.5 h-3.5 text-t1 flex-shrink-0" />
        <span className="font-mono text-t1 truncate">{r.filePath}</span>
        <span className="text-t4 flex-shrink-0">:{r.startLine}–{r.endLine}</span>
        {r.score !== undefined && (
          <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded font-mono flex-shrink-0 text-t3"
            style={{ backgroundColor: 'var(--s4)' }}>
            {(r.score * 100).toFixed(0)}% match
          </span>
        )}
        {open
          ? <ChevronUp className="w-3.5 h-3.5 text-t4 ml-1 flex-shrink-0" />
          : <ChevronDown className="w-3.5 h-3.5 text-t4 ml-1 flex-shrink-0" />
        }
      </button>
      {open && (
        <div className="relative">
          <button
            onClick={copyContent}
            className="absolute top-2 right-2 z-10 p-1.5 rounded text-t3 transition-colors"
            style={{ backgroundColor: 'var(--s4)' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--s5)')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'var(--s4)')}
          >
            {copied ? <CheckIcon className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
          </button>
          <SyntaxHighlighter
            language={lang}
            style={oneDark}
            customStyle={{ margin: 0, borderRadius: 0, fontSize: '0.75rem', background: '#0d1117' }}
          >
            {r.content}
          </SyntaxHighlighter>
        </div>
      )}
    </div>
  );
}

/* ─── Message bubble ────────────────────────────────────────── */

function MessageBubble({ msg }: { msg: Message }) {
  const isUser = msg.role === 'user';

  return (
    <div className={cn('flex gap-3 mb-6', isUser ? 'flex-row-reverse' : 'flex-row')}>
      {/* Avatar */}
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-[11px] font-semibold"
        style={isUser
          ? { backgroundColor: 'var(--user-avatar-bg)', color: 'var(--user-avatar-text)' }
          : { backgroundColor: 'var(--s4)', border: '1px solid var(--b-default)', color: 'var(--t3)' }
        }
      >
        {isUser ? 'U' : 'AI'}
      </div>

      {/* Content */}
      <div className={cn('flex-1 min-w-0', isUser ? 'flex flex-col items-end' : '')}>
        <div
          className="rounded-2xl px-4 py-3 text-sm leading-relaxed max-w-[85%]"
          style={isUser ? {
            backgroundColor: 'var(--user-bubble-bg)',
            border: '1px solid var(--user-bubble-border)',
            color: 'var(--user-bubble-text)',
          } : {
            backgroundColor: 'var(--s2)',
            border: '1px solid var(--b-subtle)',
            color: 'var(--t1)',
          }}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap">{msg.content}</p>
          ) : (
            <ReactMarkdown
              className="prose dark:prose-invert prose-sm max-w-none prose-p:my-1.5 prose-pre:my-2"
              components={{
                code({ className, children, ...props }: any) {
                  const match = /language-(\w+)/.exec(className || '');
                  if (!match) {
                    return (
                      <code
                        className="px-1.5 py-0.5 rounded text-t1 text-[11px] font-mono"
                        style={{ backgroundColor: 'var(--s4)', border: '1px solid var(--b-subtle)' }}
                        {...props}
                      >
                        {children}
                      </code>
                    );
                  }
                  return (
                    <SyntaxHighlighter language={match[1]} style={oneDark} PreTag="div"
                      customStyle={{ borderRadius: '8px', fontSize: '0.75rem', background: '#0d1117', border: '1px solid rgba(255,255,255,0.08)' }}>
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  );
                },
              }}
            >
              {msg.content + (msg.streaming ? '▌' : '')}
            </ReactMarkdown>
          )}
        </div>

        {/* Code references */}
        {!isUser && msg.references && msg.references.length > 0 && (
          <div className="mt-2 space-y-1 max-w-[85%]">
            <p className="text-[11px] text-t4 px-1 flex items-center gap-1">
              <FileCode className="w-3 h-3" />
              {msg.references.length} code reference{msg.references.length !== 1 ? 's' : ''}
            </p>
            {msg.references.map((r, i) => <CodeRefCard key={i} codeRef={r} />)}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Empty state ───────────────────────────────────────────── */

function EmptyState({ onSuggest }: { onSuggest: (q: string) => void }) {
  const suggestions = [
    'Explain the authentication flow',
    'Which services handle payments?',
    'Where is user session management?',
    'What databases does this system use?',
    'Explain the queue worker architecture',
    'Who owns the API gateway service?',
  ];

  return (
    <div className="flex flex-col items-center justify-center h-full px-6 py-12 text-center">
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
        style={{ backgroundColor: 'rgba(189,187,255,0.18)', border: '1px solid rgba(189,187,255,0.35)' }}>
        <Zap className="w-6 h-6" style={{ color: 'var(--t1)' }} />
      </div>
      <h2 className="text-lg font-semibold text-t1 tracking-tight mb-2">
        Ask anything about your codebase
      </h2>
      <p className="text-sm text-t3 max-w-md leading-relaxed mb-8">
        CodePilot Engineer understands your architecture, services, and code. Ask in natural language — get answers with exact file references and context.
      </p>
      <div className="w-full max-w-lg grid grid-cols-2 gap-2">
        {suggestions.map(s => (
          <button
            key={s}
            onClick={() => onSuggest(s)}
            className="text-left px-3 py-2.5 rounded-lg text-xs text-t2 transition-all leading-relaxed border"
            style={{ borderColor: 'var(--b-subtle)', backgroundColor: 'transparent' }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'var(--b-strong)';
              e.currentTarget.style.backgroundColor = 'var(--brand-dim)';
              e.currentTarget.style.color = 'var(--t1)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'var(--b-subtle)';
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--t2)';
            }}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─── Conversation sidebar ──────────────────────────────────── */

interface ConvSidebarProps {
  conversations: Conversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onNew: () => void;
}

function ConvSidebar({ conversations, activeId, onSelect, onDelete, onNew }: ConvSidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const now = new Date();

  const grouped = conversations.reduce<{ today: Conversation[]; week: Conversation[]; older: Conversation[] }>(
    (acc, c) => {
      const d = new Date(c.updatedAt);
      const daysDiff = Math.floor((now.getTime() - d.getTime()) / 86_400_000);
      if (daysDiff < 1)      acc.today.push(c);
      else if (daysDiff < 7) acc.week.push(c);
      else                   acc.older.push(c);
      return acc;
    },
    { today: [], week: [], older: [] },
  );

  const groups = [
    { label: 'Today',     items: grouped.today },
    { label: 'This week', items: grouped.week },
    { label: 'Earlier',   items: grouped.older },
  ].filter(g => g.items.length > 0);

  if (collapsed) {
    return (
      <div
        className="flex-shrink-0 flex flex-col items-center border-r py-3 gap-2"
        style={{ width: '44px', borderColor: 'var(--b-subtle)', backgroundColor: 'var(--s0)' }}
      >
        <button
          onClick={() => setCollapsed(false)}
          title="Expand sidebar"
          className="w-7 h-7 rounded-md flex items-center justify-center transition-colors"
          style={{ color: 'var(--t3)' }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--overlay-md)'; e.currentTarget.style.color = 'var(--t1)'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--t3)'; }}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        <button
          onClick={onNew}
          title="New conversation"
          className="w-7 h-7 rounded-md flex items-center justify-center transition-opacity hover:opacity-80"
          style={{ backgroundColor: 'var(--brand)', color: '#fff' }}
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="w-[220px] flex-shrink-0 flex flex-col border-r" style={{ borderColor: 'var(--b-subtle)', backgroundColor: 'var(--s0)' }}>
      {/* Header */}
      <div className="px-3 py-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--b-subtle)' }}>
        <span className="text-xs font-semibold text-t2">Conversations</span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setCollapsed(true)}
            title="Collapse sidebar"
            className="w-6 h-6 rounded-md flex items-center justify-center transition-colors"
            style={{ color: 'var(--t4)' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--overlay-md)'; e.currentTarget.style.color = 'var(--t2)'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--t4)'; }}
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button onClick={onNew}
            className="w-6 h-6 rounded-md flex items-center justify-center transition-opacity hover:opacity-80"
            style={{ backgroundColor: 'var(--brand)', color: '#fff' }}
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto py-2 px-2">
        {groups.length === 0 ? (
          <div className="py-8 text-center">
            <MessageSquare className="w-8 h-8 text-t4 mx-auto mb-2" />
            <p className="text-xs text-t4">No conversations yet</p>
            <p className="text-[10px] text-t4 mt-1 opacity-60">Start a conversation below</p>
          </div>
        ) : (
          groups.map(g => (
            <div key={g.label} className="mb-3">
              <div className="px-2 py-1 text-[10px] text-t4 uppercase tracking-wider font-semibold">{g.label}</div>
              {g.items.map(c => (
                <div
                  key={c._id}
                  onClick={() => onSelect(c._id)}
                  className="group flex items-start gap-2 px-2 py-2 rounded-lg cursor-pointer transition-colors mb-0.5"
                  style={{
                    backgroundColor: activeId === c._id ? 'var(--s4)' : 'transparent',
                    color: activeId === c._id ? 'var(--t1)' : 'var(--t3)',
                  }}
                  onMouseEnter={e => {
                    if (activeId !== c._id) e.currentTarget.style.backgroundColor = 'var(--overlay-sm)';
                  }}
                  onMouseLeave={e => {
                    if (activeId !== c._id) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0 opacity-60" />
                  <span className="flex-1 text-[11px] truncate leading-relaxed">{c.title || 'Untitled'}</span>
                  <button
                    onClick={e => { e.stopPropagation(); onDelete(c._id); }}
                    className="opacity-0 group-hover:opacity-100 p-0.5 hover:text-red-400 transition-all flex-shrink-0"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="border-t p-3" style={{ borderColor: 'var(--b-subtle)' }}>
        <Link to="/repositories"
          className="flex items-center gap-2 text-[11px] text-t3 hover:text-t1 transition-colors">
          <GitBranch className="w-3 h-3" />
          Manage repositories
          <ChevronRight className="w-3 h-3 ml-auto" />
        </Link>
      </div>
    </div>
  );
}

/* ─── Context panel ─────────────────────────────────────────── */

interface ContextPanelProps {
  onClose: () => void;
  repo: Repo | null;
  onClearRepo: () => void;
}

function ContextPanel({ onClose, repo, onClearRepo }: ContextPanelProps) {
  return (
    <div className="w-[240px] flex-shrink-0 flex flex-col border-l" style={{ borderColor: 'var(--b-subtle)', backgroundColor: 'var(--s0)' }}>
      <div className="px-3 py-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--b-subtle)' }}>
        <span className="text-xs font-semibold text-t2">Context</span>
        <button
          onClick={onClose}
          className="p-1 rounded text-t3 hover:text-t1 transition-colors"
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--overlay-md)')}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        <div>
          <div className="text-[10px] text-t4 uppercase tracking-widest font-semibold mb-2">Active Repository</div>
          {repo ? (
            <div className="rounded-lg border p-2.5 space-y-2" style={{ backgroundColor: 'var(--overlay-sm)', borderColor: 'var(--b-default)' }}>
              <div className="flex items-center gap-2">
                <GitBranch className="w-3.5 h-3.5 text-t1 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-semibold text-t1 truncate">{repo.name}</div>
                  <div className="text-[10px] text-t4">
                    {repo.fileCount ? `${repo.fileCount} files indexed` : 'Indexed'}
                  </div>
                </div>
                <CheckCircle className="w-3 h-3 text-emerald-400 flex-shrink-0" />
              </div>
              <button
                onClick={onClearRepo}
                className="w-full text-[10px] text-t4 hover:text-t2 transition-colors text-left flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Switch to all repos
              </button>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2 p-2 rounded-lg border" style={{ backgroundColor: 'var(--s1)', borderColor: 'var(--b-subtle)' }}>
                <GitBranch className="w-3.5 h-3.5 text-t3 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-[11px] text-t1 truncate">All repositories</div>
                  <div className="text-[10px] text-t4">Auto-select best match</div>
                </div>
              </div>
              <Link
                to="/repositories"
                className="flex items-center gap-1.5 mt-2 text-[10px] text-t1 hover:opacity-70 transition-colors"
              >
                <GitBranch className="w-3 h-3" /> Pick a specific repo
                <ArrowRight className="w-3 h-3 ml-auto" />
              </Link>
            </div>
          )}
        </div>
        <div className="h-px" style={{ backgroundColor: 'var(--b-subtle)' }} />
        <div>
          <div className="text-[10px] text-t4 uppercase tracking-widest font-semibold mb-2">Architecture</div>
          <Link to="/architecture" className="flex items-center gap-2 text-[11px] text-t3 hover:text-t1 transition-colors group">
            <Share2 className="w-3.5 h-3.5" />
            View service graph
            <ArrowRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ─── Main page ─────────────────────────────────────────────── */

export function ChatPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConvId, setActiveConvId] = useState<string | null>(searchParams.get('conv'));
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState(searchParams.get('q') || '');
  const [sending, setSending] = useState(false);
  const [showContext, setShowContext] = useState(false);
  // Repository context — populated from ?repo=<id> URL param
  const [selectedRepo, setSelectedRepo] = useState<Repo | null>(null);
  const selectedRepoId = searchParams.get('repo');
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Typewriter queue: incoming SSE chars are buffered here and drained smoothly
  const typewriterQueueRef = useRef<string>('');
  const typewriterActiveRef = useRef<boolean>(false);
  const typewriterMsgIdRef = useRef<string>('');
  const typewriterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadConversations = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/queries/conversations`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setConversations(data.data);
    } catch { /* ignore */ }
  }, []);

  const loadConversation = useCallback(async (id: string) => {
    try {
      const res = await fetch(`${API_URL}/queries/conversations/${id}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        setMessages(
          data.data.messages.map((m: any, i: number) => ({
            id: `${i}`,
            role: m.role,
            content: m.content,
            references: m.references,
          }))
        );
      }
    } catch { /* ignore */ }
  }, []);

  // Fetch repo details whenever ?repo= changes
  useEffect(() => {
    if (!selectedRepoId) { setSelectedRepo(null); return; }
    fetch(`${API_URL}/repositories/${selectedRepoId}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { if (d.success) setSelectedRepo(d.data); })
      .catch(() => {});
  }, [selectedRepoId]);

  // Auto-open context panel when a repo is pre-selected
  useEffect(() => { if (selectedRepoId) setShowContext(true); }, [selectedRepoId]);

  useEffect(() => { loadConversations(); }, [loadConversations]);
  useEffect(() => { if (activeConvId) loadConversation(activeConvId); }, [activeConvId, loadConversation]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  function handleInput(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
  }

  /** Drains the typewriter queue at ~8ms per tick, 6-8 chars at a time */
  function startTypewriter(msgId: string) {
    if (typewriterActiveRef.current) return;
    typewriterActiveRef.current = true;
    typewriterMsgIdRef.current = msgId;

    function tick() {
      const queue = typewriterQueueRef.current;
      if (queue.length === 0) {
        // Nothing pending — pause; will be re-triggered on next push
        typewriterActiveRef.current = false;
        return;
      }
      // Drain 6-8 chars per frame — fast but still visibly streaming
      const chunkSize = Math.min(8, queue.length);
      const chars = queue.slice(0, chunkSize);
      typewriterQueueRef.current = queue.slice(chunkSize);

      setMessages(prev =>
        prev.map(m =>
          m.id === typewriterMsgIdRef.current
            ? { ...m, content: m.content + chars }
            : m
        )
      );

      typewriterTimerRef.current = setTimeout(tick, 8);
    }

    tick();
  }

  /** Push text into the queue and (re)start draining if needed */
  function pushToTypewriter(text: string, msgId: string) {
    typewriterQueueRef.current += text;
    if (!typewriterActiveRef.current) {
      startTypewriter(msgId);
    }
  }

  /** Wait until the typewriter queue is fully drained */
  function waitForTypewriterDrain(): Promise<void> {
    return new Promise(resolve => {
      function check() {
        if (typewriterQueueRef.current.length === 0 && !typewriterActiveRef.current) {
          resolve();
        } else {
          setTimeout(check, 20);
        }
      }
      check();
    });
  }

  async function sendMessage(query?: string) {
    const q = query ?? input.trim();
    if (!q || sending) return;

    setInput('');
    if (textareaRef.current) { textareaRef.current.style.height = 'auto'; }
    setSending(true);

    const userMsg: Message = { id: Date.now().toString(), role: 'user', content: q };
    const aiMsgId = `${Date.now()}-ai`;
    const aiMsg: Message   = { id: aiMsgId, role: 'assistant', content: '', streaming: true };
    setMessages(prev => [...prev, userMsg, aiMsg]);

    // Reset typewriter state
    if (typewriterTimerRef.current) clearTimeout(typewriterTimerRef.current);
    typewriterQueueRef.current = '';
    typewriterActiveRef.current = false;
    typewriterMsgIdRef.current = aiMsgId;

    abortRef.current = new AbortController();

    try {
      const res = await fetch(`${API_URL}/queries`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          query: q,
          ...(activeConvId && { conversationId: activeConvId }),
          // Pass the selected repo so the backend filters Pinecone to that repo's vectors
          ...(selectedRepoId && { repositoryId: selectedRepoId }),
        }),
        signal: abortRef.current.signal,
      });

      if (!res.ok) {
        // Surface HTTP errors (401, 403, 404, 500…) with proper message
        let errMsg = `Request failed (${res.status})`;
        try {
          const errData = await res.json();
          errMsg = errData.error?.message || errData.message || errMsg;
        } catch { /* ignore parse error */ }
        throw new Error(errMsg);
      }

      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let payload: any;
          try { payload = JSON.parse(line.slice(6)); } catch { continue; }

          if (payload.type === 'start') {
            setActiveConvId(payload.conversationId);
          } else if (payload.type === 'delta' && payload.text) {
            // Push to typewriter queue instead of setting state directly
            pushToTypewriter(payload.text, aiMsgId);
          } else if (payload.type === 'error') {
            throw new Error(payload.message || 'AI service error');
          } else if (payload.type === 'done') {
            // Wait for any buffered chars to finish rendering before marking done
            await waitForTypewriterDrain();
            setMessages(prev =>
              prev.map(m => m.id === aiMsgId ? { ...m, streaming: false, references: payload.references } : m)
            );
            loadConversations();
          }
        }
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        // Clear any pending typewriter content on error
        if (typewriterTimerRef.current) clearTimeout(typewriterTimerRef.current);
        typewriterQueueRef.current = '';
        typewriterActiveRef.current = false;
        setMessages(prev =>
          prev.map(m => m.id === aiMsgId
            ? { ...m, content: m.content || `Error: ${err.message}`, streaming: false }
            : m)
        );
      }
    } finally {
      setSending(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    sendMessage();
  }

  async function deleteConversation(id: string) {
    await fetch(`${API_URL}/queries/conversations/${id}`, { method: 'DELETE', headers: authHeaders() });
    setConversations(prev => prev.filter(c => c._id !== id));
    if (activeConvId === id) { setActiveConvId(null); setMessages([]); }
  }

  function newConversation() {
    abortRef.current?.abort();
    setActiveConvId(null);
    setMessages([]);
    setInput('');
  }

  function clearRepo() {
    // Remove ?repo from URL, keep other params
    const next = new URLSearchParams(searchParams);
    next.delete('repo');
    setSearchParams(next, { replace: true });
    setSelectedRepo(null);
  }

  return (
    <div className="flex h-full" style={{ backgroundColor: 'var(--s0)' }}>
      <SEO title="AI Chat" description="Ask questions about your codebase in natural language. Get answers grounded in your actual code with exact file references and architecture context." noIndex />
      {/* Conversation sidebar */}
      <ConvSidebar
        conversations={conversations}
        activeId={activeConvId}
        onSelect={id => { setActiveConvId(id); setMessages([]); }}
        onDelete={deleteConversation}
        onNew={newConversation}
      />

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Inner top bar */}
        <div className="h-11 flex-shrink-0 border-b px-4 flex items-center justify-between" style={{ borderColor: 'var(--b-subtle)' }}>
          <div className="flex items-center gap-2 min-w-0">
            {activeConvId ? (
              <>
                <MessageSquare className="w-3.5 h-3.5 text-t3 flex-shrink-0" />
                <span className="text-sm text-t2 truncate">
                  {conversations.find(c => c._id === activeConvId)?.title || 'Conversation'}
                </span>
              </>
            ) : (
              <span className="text-sm text-t4">New conversation</span>
            )}
            {/* Repo badge — shown when a repo is scoped */}
            {selectedRepo && (
              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-lg text-[10px] font-medium ml-1"
                style={{ backgroundColor: 'rgba(189,187,255,0.18)', border: '1px solid rgba(189,187,255,0.35)', color: 'var(--t1)' }}>
                <GitBranch className="w-3 h-3" />
                {selectedRepo.name}
                <button onClick={clearRepo} className="hover:opacity-70 transition-colors ml-0.5">
                  <X className="w-2.5 h-2.5" />
                </button>
              </div>
            )}
          </div>
          <button
            onClick={() => setShowContext(s => !s)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs transition-colors"
            style={showContext
              ? { backgroundColor: 'var(--overlay-md)', color: 'var(--t1)', border: '1px solid var(--b-default)' }
              : { color: 'var(--t3)', backgroundColor: 'transparent', border: '1px solid transparent' }
            }
            onMouseEnter={e => {
              if (!showContext) {
                e.currentTarget.style.backgroundColor = 'var(--overlay-md)';
                e.currentTarget.style.color = 'var(--t1)';
              }
            }}
            onMouseLeave={e => {
              if (!showContext) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--t3)';
              }
            }}
          >
            <Share2 className="w-3 h-3" />
            Context
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {messages.length === 0 ? (
            <EmptyState onSuggest={q => sendMessage(q)} />
          ) : (
            <>
              {messages.map(msg => <MessageBubble key={msg.id} msg={msg} />)}
              {sending && (
                <div className="flex items-center gap-2 px-4 py-3 text-xs text-t4">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Analyzing your codebase…
                </div>
              )}
              <div ref={bottomRef} />
            </>
          )}
        </div>

        {/* Input */}
        <div className="flex-shrink-0 border-t p-4" style={{ borderColor: 'var(--b-subtle)' }}>
          <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
            <div
              className="flex items-end gap-3 rounded-xl p-3 transition-all"
              style={{ backgroundColor: 'var(--s2)', border: '1px solid var(--b-default)' }}
              onFocusCapture={e => {
                const el = e.currentTarget;
                el.style.borderColor = 'var(--brand-light)';
                el.style.boxShadow = '0 0 0 3px rgba(189,187,255,0.25)';
              }}
              onBlurCapture={e => {
                const el = e.currentTarget;
                el.style.borderColor = 'var(--b-default)';
                el.style.boxShadow = 'none';
              }}
            >
              <textarea
                ref={textareaRef}
                value={input}
                onChange={handleInput}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
                }}
                placeholder="Ask anything about your codebase… (Shift+Enter for newline)"
                rows={1}
                className="flex-1 resize-none bg-transparent text-sm text-t1 placeholder-t4 focus:outline-none leading-relaxed"
                style={{ minHeight: '22px', maxHeight: '160px', color: 'var(--t1)' }}
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                className="flex-shrink-0 w-8 h-8 rounded-lg hover:opacity-80 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-opacity"
                style={{ backgroundColor: 'var(--brand)', color: '#fff' }}
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
            <p className="text-center text-[10px] text-t4 mt-2">
              {selectedRepo
                ? <>Searching within <span className="text-t1 font-medium">{selectedRepo.name}</span> · {selectedRepo.fileCount ?? 0} files indexed</>
                : 'Answers reference actual code from your indexed repositories.'}
            </p>
          </form>
        </div>
      </div>

      {/* Context panel */}
      {showContext && <ContextPanel onClose={() => setShowContext(false)} repo={selectedRepo} onClearRepo={clearRepo} />}
    </div>
  );
}
